import { formatCurrency } from "../../helpers/estimateTotals";
import {
  getPayrollOverallSummaryPresentation,
  getPayrollTotalPresentation,
} from "../../helpers/payrollReport";
import { REPORT_HEADER_SHELL } from "../../helpers/reportHeader";
import {
  PDF_COLORS,
  PDF_PAGE_MARGINS,
  PDF_STYLES,
  pdfGoldRule,
} from "../shared/pdfTheme";
import {
  TABLE_LAYOUT,
  buildReportBrandHeader,
  buildItemRow,
  buildNotesSection,
  buildStandardFooter,
  buildTotalsPanel,
} from "../shared/pdfDocumentParts";

const LANDSCAPE_CONTENT_WIDTH = 702;
const DETAIL_COLUMN_WIDTHS = {
  date: 52,
  trade: 84,
  classification: 90,
  payrollType: 100,
  hours: 38,
  break: 38,
  rate: 48,
  gross: 48,
};
const PAYROLL_DETAIL_CELL_STYLE = "payrollDetailCell";

function buildPayrollSummarySection(summary) {
  if (!summary) return [];

  const cards = [
    { label: "Total Entries", value: String(summary.entryCount), emphasis: false },
    {
      label: "Regular Hours",
      value: Number(summary.regularHours || 0).toFixed(2),
      emphasis: false,
    },
  ];

  if (Number(summary.overtimeHours || 0) > 0) {
    cards.push({
      label: "Overtime Hours",
      value: Number(summary.overtimeHours).toFixed(2),
      emphasis: false,
    });
  }

  cards.push(
    {
      label: "Break Hours",
      value: Number(summary.breakHours || 0).toFixed(2),
      emphasis: false,
    },
    {
      label: "Gross Payroll",
      value: formatCurrency(summary.grossPay || 0),
      emphasis: true,
    },
  );

  return [
    pdfGoldRule(LANDSCAPE_CONTENT_WIDTH),
    { text: "Payroll Summary", style: "sectionTitle" },
    {
      columns: cards.map((card) => ({
        width: "*",
        stack: [
          { text: card.label, style: "summaryCardLabel" },
          {
            text: card.value,
            style: card.emphasis ? "summaryCardValueEmphasis" : "summaryCardValue",
          },
        ],
        border: [true, true, true, true],
        borderColor: PDF_COLORS.border,
        fillColor: PDF_COLORS.panelBg,
        margin: [4, 8, 4, 8],
      })),
      columnGap: 8,
    },
  ];
}

function buildDetailTable(rows, options) {
  if (!rows?.length) return null;

  const showProject = options?.showProjectNames;
  const showCategories = options?.showWorkCategories;
  const showTrade = options?.showTrade;
  const showBreaks = options?.showBreaks;
  const showRates = options?.showHourlyRates;

  const headerRow = [{ text: "Date", style: "tableHeader" }];
  const widths = [DETAIL_COLUMN_WIDTHS.date];
  const cellStyle = PAYROLL_DETAIL_CELL_STYLE;

  if (showProject) {
    headerRow.push({ text: "Project", style: "tableHeader" });
    widths.push("*");
  }

  if (showTrade) {
    headerRow.push({ text: "Trade", style: "tableHeader" });
    widths.push(DETAIL_COLUMN_WIDTHS.trade);
  }

  if (showCategories) {
    headerRow.push({ text: "Classification", style: "tableHeader" });
    headerRow.push({ text: "Payroll Type", style: "tableHeader" });
    widths.push(
      DETAIL_COLUMN_WIDTHS.classification,
      DETAIL_COLUMN_WIDTHS.payrollType,
    );
  }

  headerRow.push({ text: "Hours", style: "tableHeader", alignment: "right" });
  widths.push(DETAIL_COLUMN_WIDTHS.hours);

  if (showBreaks) {
    headerRow.push({ text: "Break", style: "tableHeader", alignment: "right" });
    widths.push(DETAIL_COLUMN_WIDTHS.break);
  }

  if (showRates) {
    headerRow.push({ text: "Rate", style: "tableHeader", alignment: "right" });
    widths.push(DETAIL_COLUMN_WIDTHS.rate);
  }

  headerRow.push({ text: "Gross", style: "tableHeader", alignment: "right" });
  widths.push(DETAIL_COLUMN_WIDTHS.gross);

  const bodyRows = rows.map((row) => {
    const cells = [{ text: row.date, style: cellStyle }];

    if (showProject) {
      cells.push({ text: row.project || "—", style: cellStyle });
    }

    if (showTrade) {
      cells.push({
        text: row.trade || "—",
        style: cellStyle,
        noWrap: true,
      });
    }

    if (showCategories) {
      cells.push({
        text: row.classification || "—",
        style: cellStyle,
        noWrap: true,
      });
      cells.push({
        text: row.payrollType || "—",
        style: cellStyle,
        noWrap: true,
      });
    }

    cells.push({
      text: Number(row.hours || 0).toFixed(2),
      style: cellStyle,
      alignment: "right",
    });

    if (showBreaks) {
      cells.push({
        text:
          row.breakHours != null ? Number(row.breakHours).toFixed(2) : "—",
        style: cellStyle,
        alignment: "right",
      });
    }

    if (showRates) {
      cells.push({
        text: row.rate != null ? formatCurrency(row.rate) : "—",
        style: cellStyle,
        alignment: "right",
      });
    }

    cells.push({
      text: formatCurrency(row.gross || 0),
      style: cellStyle,
      alignment: "right",
    });

    return cells;
  });

  return {
    table: {
      headerRows: 1,
      widths,
      body: [headerRow, ...bodyRows],
    },
    layout: TABLE_LAYOUT,
    margin: [0, 4, 0, 0],
  };
}

function buildDetailSection(detail, options) {
  if (!detail || detail.mode === "hidden") return [];

  const content = [
    pdfGoldRule(LANDSCAPE_CONTENT_WIDTH),
    { text: detail.summaryLabel, style: "sectionTitle" },
  ];

  if (detail.mode === "summary") {
    detail.summaryRows.forEach((row) => {
      content.push(
        buildItemRow({
          name: row.label,
          detail: row.detail,
          amount: row.amount,
        }),
      );
    });
    return content;
  }

  const table = buildDetailTable(detail.detailRows, options);
  if (table) {
    content.push(table);
  }

  return content;
}

function buildPayrollTotalsSection(reportModel) {
  const presentation = getPayrollTotalPresentation(reportModel);
  if (!presentation) return [];

  const rows = presentation.lines.map((line) => ({
    columns: [
      {
        width: "*",
        text: line.label,
        style: line.emphasis ? "totalsGrand" : "totalsRow",
      },
      {
        width: 90,
        text: line.value,
        style: line.emphasis ? "totalsGrand" : "totalsRow",
        alignment: "right",
      },
    ],
  }));

  return [pdfGoldRule(LANDSCAPE_CONTENT_WIDTH), buildTotalsPanel(rows)];
}

function buildOverallSummarySection(reportModel) {
  const presentation = getPayrollOverallSummaryPresentation(reportModel);
  if (!presentation) return [];

  const rows = presentation.lines.map((line) => ({
    columns: [
      {
        width: "*",
        text: line.label,
        style: line.emphasis ? "totalsGrand" : "totalsRow",
      },
      {
        width: 90,
        text: line.value,
        style: line.emphasis ? "totalsGrand" : "totalsRow",
        alignment: "right",
      },
    ],
  }));

  return [
    pdfGoldRule(LANDSCAPE_CONTENT_WIDTH),
    { text: presentation.title, style: "sectionTitle" },
    buildTotalsPanel(rows),
  ];
}

function buildSingleEmployeePayrollPdfContent(sectionModel, { logoDataUrl, branding } = {}) {
  const { header, summary, detail, options } = sectionModel;

  return [
    ...buildReportBrandHeader({
      logoDataUrl,
      branding,
      ...REPORT_HEADER_SHELL.payroll,
      fields: header.fields,
    }),
    ...buildPayrollSummarySection(summary),
    ...buildDetailSection(detail, options),
    ...buildPayrollTotalsSection(sectionModel),
  ];
}

export function buildPayrollPdfDocumentDefinition(
  reportModel,
  { logoDataUrl, branding } = {},
) {
  if (!reportModel) {
    throw new Error("Report model is required");
  }

  const { header, notes } = reportModel;
  const employeeLabel = header?.employeeName || "Report";

  let content;

  if (reportModel.isGrouped) {
    content = [];

    reportModel.sections.forEach((section, index) => {
      if (index > 0) {
        content.push({ text: "", pageBreak: "before" });
      }

      content.push(
        ...buildSingleEmployeePayrollPdfContent(
          {
            ...section,
            options: reportModel.options,
          },
          { logoDataUrl, branding },
        ),
      );
    });

    content.push(...buildOverallSummarySection(reportModel));
    content.push(...(buildNotesSection(notes) || []));
  } else {
    content = [
      ...buildSingleEmployeePayrollPdfContent(reportModel, { logoDataUrl, branding }),
      ...(buildNotesSection(notes) || []),
    ];
  }

  return {
    info: {
      title: `Payroll Report — ${employeeLabel}`,
      author: branding?.pdfAuthor || "Organization",
      subject: header?.periodLabel || "Payroll",
    },
    pageSize: "LETTER",
    pageOrientation: "landscape",
    pageMargins: PDF_PAGE_MARGINS,
    defaultStyle: {
      font: "Roboto",
      fontSize: 9,
      color: PDF_COLORS.text,
      lineHeight: 1.25,
    },
    styles: {
      ...PDF_STYLES,
      payrollDetailCell: {
        fontSize: 7.5,
        color: PDF_COLORS.text,
      },
    },
    footer: buildStandardFooter(
      header?.generatedDate,
      branding?.footerText || "Organization",
    ),
    content,
  };
}
